import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

export default function CursorFollower({ dark }) {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if device is mobile/touch device
    const checkMobile = () => {
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 768;
      // Only consider it mobile if it's both touch device AND small screen
      setIsMobile(isTouchDevice && isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Don't add mouse event listeners on mobile
    if (isMobile) {
      return () => {
        window.removeEventListener('resize', checkMobile);
      };
    }

    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleMouseEnter = () => {
      setIsHovering(true);
    };

    const handleMouseLeave = () => {
      setIsHovering(false);
    };

    // Add event listeners
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseenter", handleMouseEnter);
    document.addEventListener("mouseleave", handleMouseLeave);

    // Add hover detection for interactive elements
    const interactiveElements = document.querySelectorAll('a, button, input, textarea, select, [role="button"]');
    
    interactiveElements.forEach(el => {
      el.addEventListener('mouseenter', () => setIsHovering(true));
      el.addEventListener('mouseleave', () => setIsHovering(false));
    });

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseenter", handleMouseEnter);
      document.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener('resize', checkMobile);
      
      interactiveElements.forEach(el => {
        el.removeEventListener('mouseenter', () => setIsHovering(true));
        el.removeEventListener('mouseleave', () => setIsHovering(false));
      });
    };
  }, [isMobile]);

  // Don't render cursor follower on mobile
  if (isMobile) {
    return null;
  }

  return (
    <>
      {/* Main cursor dot */}
      <motion.div
        className={`fixed top-0 left-0 w-4 h-4 rounded-full pointer-events-none z-[60] ${
          dark ? 'bg-indigo-400' : 'bg-indigo-600'
        } mix-blend-difference`}
        animate={{
          x: mousePosition.x - 8,
          y: mousePosition.y - 8,
          scale: isHovering ? 1.5 : 1,
        }}
        transition={{
          type: "spring",
          stiffness: 500,
          damping: 28,
          mass: 0.5,
        }}
      />

      {/* Cursor ring */}
      <motion.div
        className={`fixed top-0 left-0 w-8 h-8 rounded-full border-2 pointer-events-none z-[59] ${
          dark ? 'border-indigo-400/50' : 'border-indigo-600/50'
        }`}
        animate={{
          x: mousePosition.x - 16,
          y: mousePosition.y - 16,
          scale: isHovering ? 2 : 1,
          opacity: isHovering ? 0.8 : 0.3,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 25,
          mass: 0.8,
        }}
      />

      {/* Cursor trail dots */}
      {[...Array(3)].map((_, index) => (
        <motion.div
          key={index}
          className={`fixed top-0 left-0 w-2 h-2 rounded-full pointer-events-none z-[58] ${
            dark ? 'bg-indigo-300/30' : 'bg-indigo-500/30'
          }`}
          animate={{
            x: mousePosition.x - 4,
            y: mousePosition.y - 4,
            scale: isHovering ? 0.8 : 0.5,
            opacity: isHovering ? 0.6 : 0.2,
          }}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 20,
            mass: 1,
            delay: index * 0.05,
          }}
        />
      ))}
    </>
  );
} 